import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import api from '../../api';

const MESES = [
  '',
  'Enero',
  'Febrero',
  'Marzo',
  'Abril',
  'Mayo',
  'Junio',
  'Julio',
  'Agosto',
  'Septiembre',
  'Octubre',
  'Noviembre',
  'Diciembre'
];

function mesAnioCalendario(date = new Date()) {
  return { mes: date.getMonth() + 1, anio: date.getFullYear() };
}

const fmtMonto = (n) => {
  if (n == null || n === '') return '—';
  return new Intl.NumberFormat('es-CL', {
    style: 'currency',
    currency: 'CLP',
    maximumFractionDigits: 0
  }).format(n);
};

function rowKey(r) {
  return String(r.clienteId);
}

export default function InformeIvaConfigRapida() {
  const [{ mes, anio }, setPeriodo] = useState(() => mesAnioCalendario());
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [draft, setDraft] = useState({});
  const [filtro, setFiltro] = useState('');
  const [saving, setSaving] = useState(false);
  const [dirtyIds, setDirtyIds] = useState(new Set());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/informes-iva/config-rapida/${anio}/${mes}`);
      setRows(data.rows || []);
      setDraft({});
      setDirtyIds(new Set());
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, [mes, anio]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const q = filtro.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        r.nombre?.toLowerCase().includes(q) ||
        r.rut?.toLowerCase().includes(q) ||
        r.asignado?.toLowerCase().includes(q)
    );
  }, [rows, filtro]);

  const getDraft = (r) => {
    const id = rowKey(r);
    return draft[id] || {};
  };

  const mergeRow = (r) => {
    const d = getDraft(r);
    return {
      ...r,
      activoInformes: d.activoInformes !== undefined ? d.activoInformes : r.activoInformes,
      ppmPorcentaje: d.ppmPorcentaje !== undefined ? d.ppmPorcentaje : r.ppmPorcentaje,
      ppmBasisMode: d.ppmBasisMode !== undefined ? d.ppmBasisMode : r.ppmBasisMode,
      iuscEnabled:
        d.iuscEnabled !== undefined ? d.iuscEnabled : Boolean(r.monthlyTaxConfig?.iusc),
      iuscAmount: d.iuscAmount !== undefined ? d.iuscAmount : r.iuscAmount,
      emailCliente: d.emailCliente !== undefined ? d.emailCliente : r.emailCliente
    };
  };

  const patchDraft = (clienteId, patch) => {
    const id = rowKey({ clienteId });
    setDraft((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }));
    setDirtyIds((prev) => new Set(prev).add(id));
  };

  const guardarFila = async (r) => {
    const m = mergeRow(r);
    setSaving(true);
    try {
      await api.put(`/informes-iva/config-rapida/${anio}/${mes}/batch`, {
        updates: [
          {
            clienteId: r.clienteId,
            activoInformes: m.activoInformes,
            ppmPorcentaje: m.ppmPorcentaje === '' ? null : m.ppmPorcentaje,
            ppmBasisMode: m.ppmBasisMode,
            emailCliente: m.emailCliente,
            monthlyTaxConfig: {
              ...r.monthlyTaxConfig,
              iusc: m.iuscEnabled
            },
            iuscAmount: m.iuscEnabled ? m.iuscAmount : undefined
          }
        ]
      });
      toast.success(`${r.nombre}: guardado`);
      setDirtyIds((prev) => {
        const n = new Set(prev);
        n.delete(rowKey(r));
        return n;
      });
      await load();
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    } finally {
      setSaving(false);
    }
  };

  const guardarTodos = async () => {
    const toSave = rows.filter((r) => dirtyIds.has(rowKey(r)));
    if (!toSave.length) {
      toast.message('No hay cambios pendientes');
      return;
    }
    setSaving(true);
    try {
      const updates = toSave.map((r) => {
        const m = mergeRow(r);
        return {
          clienteId: r.clienteId,
          activoInformes: m.activoInformes,
          ppmPorcentaje: m.ppmPorcentaje === '' ? null : m.ppmPorcentaje,
          ppmBasisMode: m.ppmBasisMode,
          emailCliente: m.emailCliente,
          monthlyTaxConfig: { ...r.monthlyTaxConfig, iusc: m.iuscEnabled },
          iuscAmount: m.iuscEnabled ? m.iuscAmount : undefined
        };
      });
      await api.put(`/informes-iva/config-rapida/${anio}/${mes}/batch`, { updates });
      toast.success(`${updates.length} empresa(s) actualizadas`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-[1400px] mx-auto px-4 py-6">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <Link to="/informes-iva" className="text-sm text-indigo-700 hover:underline">
            ← Volver a operación (informes)
          </Link>
          <h1 className="text-xl font-semibold text-slate-900 mt-1">Configuración rápida — Informes IVA</h1>
          <p className="text-sm text-slate-500 mt-1">
            Edite varias empresas en una sola tabla. El remanente 504 se obtiene del SII al calcular; aquí configura PPM,
            IUSC del período y datos de envío.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select
            className="border rounded-lg px-3 py-2 text-sm"
            value={mes}
            onChange={(e) => setPeriodo((p) => ({ ...p, mes: Number(e.target.value) }))}
          >
            {MESES.slice(1).map((nombre, i) => (
              <option key={nombre} value={i + 1}>
                {nombre}
              </option>
            ))}
          </select>
          <input
            type="number"
            className="border rounded-lg px-3 py-2 text-sm w-24"
            value={anio}
            onChange={(e) => setPeriodo((p) => ({ ...p, anio: Number(e.target.value) }))}
          />
          <button
            type="button"
            disabled={saving || dirtyIds.size === 0}
            onClick={guardarTodos}
            className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg disabled:opacity-40"
          >
            Guardar cambios ({dirtyIds.size})
          </button>
        </div>
      </div>

      <input
        className="w-full max-w-md border rounded-lg px-3 py-2 text-sm mb-4"
        placeholder="Buscar empresa, RUT o asignado…"
        value={filtro}
        onChange={(e) => setFiltro(e.target.value)}
      />

      {loading ? (
        <p className="text-slate-500">Cargando…</p>
      ) : (
        <div className="overflow-x-auto border rounded-xl bg-white shadow-sm">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase text-slate-600">
              <tr>
                <th className="p-3">Activo</th>
                <th className="p-3 min-w-[180px]">Empresa</th>
                <th className="p-3">PPM %</th>
                <th className="p-3">Base PPM</th>
                <th className="p-3">IUSC</th>
                <th className="p-3">IUSC $ ({MESES[mes]})</th>
                <th className="p-3">504</th>
                <th className="p-3 min-w-[160px]">Email informe</th>
                <th className="p-3" />
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => {
                const m = mergeRow(r);
                const dirty = dirtyIds.has(rowKey(r));
                const rem = r.remanente504;
                const remLabel =
                  rem?.status === 'CONFIRMED'
                    ? `✓ ${fmtMonto(rem.amount)}`
                    : rem?.status === 'ESTIMATED'
                      ? `~ ${fmtMonto(rem.amount)}`
                      : 'Sync F29';
                return (
                  <tr key={rowKey(r)} className={`border-t ${dirty ? 'bg-amber-50/50' : ''}`}>
                    <td className="p-2">
                      <input
                        type="checkbox"
                        checked={Boolean(m.activoInformes)}
                        onChange={(e) => patchDraft(r.clienteId, { activoInformes: e.target.checked })}
                      />
                    </td>
                    <td className="p-2">
                      <div className="font-medium text-slate-900">{r.nombre}</div>
                      <div className="text-xs text-slate-500">{r.rut}</div>
                    </td>
                    <td className="p-2">
                      <input
                        type="number"
                        step="0.01"
                        className="w-20 border rounded px-2 py-1 text-xs"
                        value={m.ppmPorcentaje ?? ''}
                        onChange={(e) =>
                          patchDraft(r.clienteId, {
                            ppmPorcentaje: e.target.value === '' ? '' : Number(e.target.value)
                          })
                        }
                      />
                    </td>
                    <td className="p-2">
                      <select
                        className="border rounded px-1 py-1 text-xs max-w-[120px]"
                        value={m.ppmBasisMode}
                        onChange={(e) => patchDraft(r.clienteId, { ppmBasisMode: e.target.value })}
                      >
                        <option value="SII_PROPOSAL">SII</option>
                        <option value="RCV_NET_SALES">RCV</option>
                        <option value="MANUAL">Manual</option>
                      </select>
                    </td>
                    <td className="p-2 text-center">
                      <input
                        type="checkbox"
                        checked={Boolean(m.iuscEnabled)}
                        onChange={(e) => patchDraft(r.clienteId, { iuscEnabled: e.target.checked })}
                      />
                    </td>
                    <td className="p-2">
                      {m.iuscEnabled ? (
                        <input
                          type="number"
                          className="w-28 border rounded px-2 py-1 text-xs"
                          placeholder="cód. 48"
                          value={m.iuscAmount ?? ''}
                          onChange={(e) =>
                            patchDraft(r.clienteId, {
                              iuscAmount: e.target.value === '' ? '' : Number(e.target.value)
                            })
                          }
                        />
                      ) : (
                        <span className="text-xs text-slate-400">—</span>
                      )}
                    </td>
                    <td className="p-2 text-xs whitespace-nowrap">{remLabel}</td>
                    <td className="p-2">
                      <input
                        type="email"
                        className="w-full min-w-[140px] border rounded px-2 py-1 text-xs"
                        value={m.emailCliente || ''}
                        onChange={(e) => patchDraft(r.clienteId, { emailCliente: e.target.value })}
                      />
                    </td>
                    <td className="p-2">
                      <button
                        type="button"
                        disabled={saving}
                        onClick={() => guardarFila(r)}
                        className="text-xs px-2 py-1 border rounded hover:bg-slate-50 whitespace-nowrap"
                      >
                        Guardar
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {filtered.length === 0 && <p className="p-6 text-center text-slate-500">Sin resultados</p>}
        </div>
      )}

      <p className="text-xs text-slate-500 mt-4">
        UTM para reajuste 504: valores oficiales SII 2026 (
        <a
          href="https://www.sii.cl/valores_y_fechas/utm/utm2026.htm"
          target="_blank"
          rel="noreferrer"
          className="text-indigo-600 underline"
        >
          tabla UTM
        </a>
        ). Ej. sept→oct 2026: 71.721 → 72.151.
      </p>
    </div>
  );
}
